// ExerciseTracker.js
import React, {
  useState,
  useRef,
  useEffect,
  Fragment,
  useCallback,
} from "react";

import { useLocation, useNavigate } from "react-router-dom";

import {
  armExercise,
  bearExercise,
  bicycleExercise,
  burpeeExercise,
  calfExercise,
  crunchExercise,
  exerciseStep1,
  exerciseStep2,
  gluteExercise,
  jumpingExercise,
  kneePushExercise,
  legExercise,
  lungeExercise,
  mountainExercise,
  pullExercise,
  PushUpExercise,
  squatExercise,
  squatExercise2,
  stepExercise,
  tricepExercise,
  wallExercise,
} from "../../assets/images";
import {
  pushupVideo,
  squatVideo,
  lungeVideo,
  tricepVideo,
  mountainVideo,
  burpeeVideo,
  calfVideo,
  kneePushVideo,
  crunchVideo,
  gluteVideo,
  jumpingVideo,
  legVideo,
  stepVideo,
  squatVideo2,
  bicycleVideo,
  wallVideo,
  armVideo,
  bearVideo,
  pullVideo,
  reverseLungeVideo,
} from "../../assets/videos";
import "../../styles/exercise.scss";
import CustomButton from "../reusable/CustomButton";
import { useSocket } from "../../context/socketConnection";
import { toast } from "react-toastify";
import { validateExerciseName } from "../../constants/constants";
import LoopVideo from "../reusable/ExerciseVideo";

const SKIP_STORAGE_KEY = "skipSteps";


const getSkippedExercises = () => {
  let raw = null;
  try {
    raw = JSON.parse(localStorage.getItem(SKIP_STORAGE_KEY));
  } catch (e) {
    raw = null;
  }
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
};

const ExerciseTracker = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const stream = useRef(null);

  const socket = useSocket();
  console.log("socket in exercise tracker page:", socket?.id);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get("type");
    if (type !== "") {
      setExercise(type);
    }
  }, [location]);

  const instruction = [
    { name: "pushup", video: pushupVideo },
    { name: "squat", video: squatVideo },
    { name: "reverse_lunge", video: reverseLungeVideo },
    { name: "lunge", video: lungeVideo },
    { name: "tricep_dips", video: tricepVideo },
    { name: "mountain_climber", video: mountainVideo },
    { name: "calf_raise", video: calfVideo },
    { name: "knee_push_up", video: kneePushVideo },
    { name: "crunch", video: crunchVideo },
    { name: "glute_bridge", video: gluteVideo },
    { name: "jumping_jack", video: jumpingVideo },
    { name: "leg_raise", video: legVideo },
    { name: "step_up", video: stepVideo },
    { name: "body_squat", video: squatVideo2 },
    { name: "bicycle_crunch", video: bicycleVideo },
    { name: "wall_sit", video: wallVideo },
    { name: "arm_circle", video: armVideo },
    { name: "bear_crawl", video: bearVideo },
    { name: "pull_up", video: pullVideo },
  ];

  const [steps, setSteps] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [skipSteps, setSkipSteps] = useState(false);
  const [skipCheck, setSkipCheck] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const instructionRef = useRef(null);

  const isRunning = useRef(null);
  const isProcessing = useRef(null);
  const [stats, setStats] = useState({
    counter: 0,
    incorrectCounter: 0,
    status: "Not started",
    form: "Checking...",
  });

  const startCamera = useCallback(async () => {
    try {
      if (!stream.current) {
        stream.current = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            facingMode: "user",
            frameRate: { ideal: 30 },
          },
        });
        isProcessing.current = true;
        isRunning.current = false;
        videoRef.current.srcObject = stream.current;
        await videoRef.current.play();
        socket.emit("start_exercise", {
          exercise_type: exercise ? exercise : "pushup",
        });
      }
    } catch (error) {
      console.error("Error starting exercise:", error);
      alert("Error starting exercise. Please try again.");
    }
  }, [exercise, socket]);

  const handleStart = async () => {
    setSteps(4);
    setIsStopped(false);
    if (skipCheck && exercise) {
      const skippedExercises = getSkippedExercises();
      skippedExercises[exercise] = true;
      localStorage.setItem(
        SKIP_STORAGE_KEY,
        JSON.stringify(skippedExercises)
      );
    }
    console.log("Exercise Start");
    
  };

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setSkipCheck(isChecked);
  };

  useEffect(() => {
    if (!exercise) return;
    const skippedExercises = getSkippedExercises();
    const skip = skippedExercises[exercise] || false;
    setSkipSteps(skip);
    if (skip && steps !== 4) {
      setSteps(4);
    }
  }, [exercise, steps]);

  useEffect(() => {
    if (steps === 4 && !stream.current) {
      startCamera();
    }
  }, [steps, startCamera]);

  useEffect(() => {
    const currentInstruction = instruction.find((itm) => itm?.name === exercise);
    setMediaReady(false);

    if (!currentInstruction?.video) {
      setMediaReady(true);
      return;
    }

    const container = instructionRef.current;
    const videoEl = container?.querySelector("video");
    if (!videoEl) return;

    if (videoEl.readyState >= 3) {
      setMediaReady(true);
      return;
    }

    const markReady = () => setMediaReady(true);
    videoEl.addEventListener("loadeddata", markReady);
    videoEl.addEventListener("canplaythrough", markReady);
    return () => {
      videoEl.removeEventListener("loadeddata", markReady);
      videoEl.removeEventListener("canplaythrough", markReady);
    };
  }, [exercise]);

  console.log("stats", stats);

  const processFrame = useCallback(() => {
    isProcessing.current = false;
    if (!isRunning.current) {
      return toast.error("process is not running");
    }
    const ctx = canvasRef.current?.getContext("2d");
    ctx?.drawImage(
      videoRef.current,
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );
    const imageData = canvasRef.current.toDataURL("image/jpeg", 0.85);
    socket.emit("process_frame", {
      frame: imageData,
    });
  }, [socket]);

  useEffect(() => {
    if (socket?.id) {
      socket.on("exercise_started", (data) => {
        console.log("Exercise session started:", data.session_id);
        isRunning.current = true;
        processFrame();
      });

      socket.on("frame_processed", (result) => {
        console.log("frame_processed result", result);
        if (result.annotated_frame) {
          const img = new Image();
          img.onload = () => {
            const ctx = canvasRef.current?.getContext("2d");
            ctx?.clearRect(
              0,
              0,
              canvasRef.current.width,
              canvasRef.current.height
            );
            ctx?.drawImage(
              img,
              0,
              0,
              canvasRef.current.width,
              canvasRef.current.height
            );
          };
          img.src = "data:image/jpeg;base64," + result.annotated_frame;
          console.log("img.src", img.src.substring(0, 100));
        }

        setStats({
          counter: result.counter || 0,
          incorrectCounter: result.incorrect_counter || 0,
          status: result.position_status || "Processing...",
          form: result.form_correct ? "Correct" : "Incorrect",
        });

        if (isRunning.current) {
          isProcessing.current = false;
          requestAnimationFrame(processFrame);
        }
      });
    }
  }, [isRunning, socket, processFrame]);

  const pauseTracking = () => {
    if (!videoRef.current || !stream.current) return;
    setIsPaused(true);
    videoRef.current.pause();
  };

  const resumeTracking = () => {
    if (!videoRef.current || !stream.current) return;
    setIsPaused(false);
    const playPromise = videoRef.current.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => { });
    }
  };

  const stopExercise = () => {
    if (stream.current) {
      stream.current.getTracks().forEach((track) => track.stop());
      stream.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setStats({
      counter: 0,
      incorrectCounter: 0,
      status: "Not started",
      form: "Checking...",
    });
    isRunning.current = false;
    isProcessing.current = false;
    setIsPaused(false);
    setIsStopped(true);
  };

  const handleRestart = async () => {
    setIsStopped(false);
    await startCamera();
  };

  const handleBack = () => {
    if (stream.current) {
      stream.current.getTracks().forEach((track) => track.stop());
      stream.current = null;
    }
    navigate(-1);
  };

  console.log("isProcessing.current", isProcessing.current);
  console.log("isRunning.current", isRunning.current);
  const currentInstruction = instruction.find((itm) => itm?.name === exercise);

  return (
    <Fragment>
      <section className="exercise_traker">
        <div className="container">
          {steps !== 4 && (
            <div className="instruction">
              <h2>Instruction</h2>
              <div className="step" ref={instructionRef}>
                {currentInstruction?.video ? (
                  <LoopVideo src={currentInstruction.video} />
                ) : (
                  <img src={currentInstruction?.image} alt={exercise} />
                )}
              </div>
            </div>
          )}

          {steps === 4 && (
            <div className="camera_feed">
              <div className="start_exercise">
                <h2>
                  {validateExerciseName(exercise)} Count: {stats?.counter}
                </h2>
                <p>Incorrect Counter: {stats?.incorrectCounter}</p>
                <p>Status: {stats.status}</p>
                <p>
                  Form:{" "}
                  <span
                    style={{
                      display: "inline-block",
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      marginRight: "10px",
                      background: stats.form === "Correct" ? "green" : "red",
                    }}
                  ></span>
                  {stats.form}
                </p>
              </div>
              <div className="screen">
                <video
                  ref={videoRef}
                  autoPlay
                  style={{
                    transform: "scaleX(-1)",
                    borderColor: 0 ? "#124b04" : "red",
                  }}
                />
                <canvas ref={canvasRef} width="640" height="480"></canvas>
              </div>
            </div>
          )}

          <div className="step_btns">
            {steps !== 4 && !skipSteps && (
              <div className="skip_steps">
                <label>
                  <input
                    type="checkbox"
                    checked={skipCheck}
                    onChange={handleCheckboxChange}
                  />
                  Skip & Never show me again
                </label>
              </div>
            )}
            <div className="controls">
              {steps !== 4 && (
                <CustomButton
                  title={mediaReady ? "Start" : "Please wait..."}
                  width="110px"
                  className="btn startBtn "
                  onClick={handleStart}
                  disabled={!mediaReady}
                />
              )}
              {steps === 4 && (
                <>
                  {isStopped ? (
                    <CustomButton
                      onClick={handleRestart}
                      width="110px"
                      className="btn"
                      title="Start"
                    />
                  ) : (
                    <>
                      {isPaused ? (
                        <CustomButton
                          onClick={resumeTracking}
                          width="110px"
                          className="btn"
                          title="Resume"
                        />
                      ) : (
                        <CustomButton
                          onClick={pauseTracking}
                          width="110px"
                          className="btn"
                          title="Pause"
                        />
                      )}
                      <CustomButton
                        onClick={stopExercise}
                        width="110px"
                        className="btn"
                        title="Stop"
                      />
                    </>
                  )}
                  <CustomButton
                    onClick={handleBack}
                    width="110px"
                    className="btn"
                    title="Back"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default ExerciseTracker;